import React, { useState, useRef, useEffect } from 'react';
import IconRenderer from '../../ui/IconRenderer';
import { ReactComponent as LearnModeIcon } from '../../../Icons/learn-mode.svg';
import { calculateCourseCost, getCookbookCost } from '../../../config/contentPricingConfig';
import './ExploreBackInfo.css';

const PROGRAMMING_LANGUAGES = [
  'JavaScript',
  'Python',
  'Java',
  'C++',
  'C#',
  'Ruby',
  'PHP',
  'Swift',
  'Go',
  'Rust',
  'TypeScript',
  'SQL',
  'HTML/CSS',
  'Shell',
  'Other'
];


const ExploreBackInfo = ({
  mode, // 'static' or 'create'
  template, // For static mode
  language,
  onLanguageChange,
  onBack,
  onBuy,
  onGenerate,
  onPreview, // New prop for navigating to preview
  tokenBalance,
  isGenerating,
  insufficientTokensError,
  getTemplateOwnershipStatus,
  onRestore
}) => {
  const [prompt, setPrompt] = useState('');
  const [topic, setTopic] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [generationType, setGenerationType] = useState('course');
  const [ownershipStatus, setOwnershipStatus] = useState({ isOwned: false, exists: false, purchaseId: null });
  const [isCheckingOwnership, setIsCheckingOwnership] = useState(false);
  const languageDropdownRef = useRef(null);


  // Update prompt when template changes in static mode
  useEffect(() => {
    if (mode === 'static' && template) {
      setPrompt(template.prompt || '');
      if (template.type === 'cookbook') {
        setGenerationType('cookbook');
      } else {
        setGenerationType('course');
      }
    }
  }, [mode, template]);

  // Check ownership status when template changes
  useEffect(() => {
    const checkOwnership = async () => {
      if (mode === 'static' && template && getTemplateOwnershipStatus) {
        setIsCheckingOwnership(true);
        try {
          const status = await getTemplateOwnershipStatus(template);
          setOwnershipStatus(status);
        } catch (error) {
          console.error('Error checking ownership status:', error);
          setOwnershipStatus({ isOwned: false, exists: false, purchaseId: null });
        } finally {
          setIsCheckingOwnership(false);
        }
      } else {
        setOwnershipStatus({ isOwned: false, exists: false, purchaseId: null });
      }
    };

    checkOwnership();
  }, [mode, template, getTemplateOwnershipStatus]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageSelect = (selectedLanguage) => {
    if (onLanguageChange) {
      onLanguageChange(selectedLanguage);
    }
    setShowLanguageDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || !onGenerate) return;

    await onGenerate({
      prompt: prompt.trim(),
      language,
      topic: topic.trim() || undefined,
      generationType
    });
  };

  const estimatedCost = generationType === 'course'
    ? calculateCourseCost('beginner') // Default to beginner cost
    : getCookbookCost(false);

  const hasSufficientTokens = tokenBalance >= estimatedCost;

  if (mode === 'static' && template) {
    // Use tokenCost from catalog item if available, otherwise calculate
    const cost = template.isCatalogItem && template.tokenCost !== undefined
      ? template.tokenCost
      : template.type === 'cookbook'
        ? getCookbookCost(false)
        : calculateCourseCost('beginner'); // Default to beginner cost

    return (
      <div className="explore-back-info-container explore-back-info-container--static">
        {/* <button
          type="button"
          className="explore-back-info-back-btn"
          onClick={onBack}
          aria-label="Back to card view"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          <span>Back</span>
        </button> */}

        <div className="explore-back-info-content">
          <div className="explore-back-info-header">
            <div className="explore-back-info-icon-wrapper">
              <IconRenderer
                name={language.toLowerCase()}
                type="language"
                size="small"
                className="explore-back-info-language-icon"
              />
            </div>
            <div className="explore-back-info-title-wrapper">
              <h3 className="explore-back-info-title">{template.title}</h3>
              <div className="explore-back-info-badges">
                <span className="explore-back-info-badge explore-back-info-badge--type">
                  {template.type === 'cookbook' ? 'Cookbook' : 'Course'}
                </span>
              </div>
            </div>
          </div>

          <div className="explore-back-info-body">
            <p className="explore-back-info-description">{template.description}</p>

            <div className="explore-back-info-meta">
              <div className="explore-back-info-meta-item">
                <span className="explore-back-info-meta-label">Language:</span>
                <span className="explore-back-info-meta-value">{template.language || language}</span>
              </div>
              <div className="explore-back-info-meta-item">
                <span className="explore-back-info-meta-label">Cost:</span>
                <span className="explore-back-info-meta-value">{cost} tokens</span>
              </div>
              {template.difficulty && (
                <div className="explore-back-info-meta-item">
                  <span className="explore-back-info-meta-label">Difficulty:</span>
                  <span className="explore-back-info-meta-value">{template.difficulty}</span>
                </div>
              )}
              {template.isCatalogItem && template.popularity !== undefined && (
                <div className="explore-back-info-meta-item">
                  <span className="explore-back-info-meta-label">Popularity:</span>
                  <span className="explore-back-info-meta-value">{template.popularity} views</span>
                </div>
              )}
            </div>
          </div>

          <div className="explore-back-info-actions">
            <div className="explore-back-info-token-info">
              <span className="explore-back-info-token-balance">
                Your tokens: <strong>{tokenBalance}</strong>
              </span>
              {!ownershipStatus.isOwned && (
                <span className="explore-back-info-token-cost">
                  Cost: <strong>{cost} tokens</strong>
                </span>
              )}
              {ownershipStatus.isOwned && ownershipStatus.exists && (
                <span className="explore-back-info-owned-indicator">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  Already Owned
                </span>
              )}
              {ownershipStatus.isOwned && !ownershipStatus.exists && (
                <span className="explore-back-info-import-indicator">
                  Available to Import
                </span>
              )}
            </div>
            <div className="explore-back-info-action-buttons">
              {template.isCatalogItem && onPreview && (
                <button
                  type="button"
                  className="explore-back-info-preview-btn"
                  onClick={onPreview}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span>Preview {template.type === 'course' ? 'Snippets' : 'Cards'}</span>
                </button>
              )}
              {ownershipStatus.isOwned && ownershipStatus.exists ? (
                <button
                  type="button"
                  className="explore-back-info-buy-btn explore-back-info-buy-btn--owned"
                  disabled
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  Already in Your Library
                </button>
              ) : ownershipStatus.isOwned && !ownershipStatus.exists ? (
                <button
                  type="button"
                  className="explore-back-info-buy-btn explore-back-info-buy-btn--import"
                  onClick={async () => {
                    if (onRestore && ownershipStatus.purchaseId) {
                      try {
                        await onRestore(ownershipStatus.purchaseId, template.title);
                        // Wait a bit for the restore to complete and content to be imported
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        // Refresh ownership status after restore
                        if (getTemplateOwnershipStatus) {
                          const status = await getTemplateOwnershipStatus(template);
                          setOwnershipStatus(status);
                        }
                      } catch (error) {
                        console.error('Error restoring purchase:', error);
                        alert(`Failed to import: ${error.message || 'Unknown error'}`);
                      }
                    }
                  }}
                  disabled={isGenerating || isCheckingOwnership}
                >
                  {isGenerating ? (
                    <>
                      <span className="spinner"></span>
                      Importing...
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                      </svg>
                      Import
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  className={`explore-back-info-buy-btn ${!hasSufficientTokens ? 'insufficient-tokens' : ''}`}
                  onClick={onBuy}
                  disabled={!hasSufficientTokens || isGenerating || isCheckingOwnership}
                  title={!hasSufficientTokens ? `You need ${cost} tokens. You have ${tokenBalance}.` : ''}
                >
                  {isGenerating ? (
                    <>
                      <span className="spinner"></span>
                      Purchasing...
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                      </svg>
                      Purchase
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {insufficientTokensError && (
            <div className="explore-back-info-token-error">
              <p>{insufficientTokensError}</p>
              <button
                type="button"
                className="explore-back-info-purchase-tokens-btn"
                onClick={() => {
                  const event = new CustomEvent('openTokenUI');
                  window.dispatchEvent(event);
                }}
              >
                Purchase Tokens
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Create mode
  return (
    <div className="explore-back-info-container explore-back-info-container--create">
      {/* <button
        type="button"
        className="explore-back-info-back-btn"
        onClick={onBack}
        aria-label="Back to card view"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        <span>Back</span>
      </button> */}

      <div className="explore-back-info-content">
        <div className="explore-back-info-create-header">
          <div className="header-title">
            <LearnModeIcon className="header-learn-icon" />
            <h3>Create a Course</h3>
          </div>
          <p>Describe your specific learning goals and we'll create a personalized course just for you.</p>
        </div>

        <form onSubmit={handleSubmit} className="explore-back-info-form">
          <div className="explore-back-info-form-group">
            <div className="explore-back-info-form-label-wrapper">
              <label htmlFor="learning-prompt" className="explore-back-info-form-label">
                Describe Course
              </label>
              {prompt.trim() && (
                <button
                  type="button"
                  className="explore-back-info-clear-btn"
                  onClick={() => setPrompt('')}
                  title="Clear input"
                  aria-label="Clear course description"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <textarea
              id="learning-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., I want to learn game physics, character collision, and procedural environments..."
              className="explore-back-info-prompt-input"
              rows={4}
              required
            />
          </div>

          <div className="explore-back-info-form-group">
            <label htmlFor="create-language" className="explore-back-info-form-label">
              Programming Language
            </label>
            <div className="custom-language-selector" ref={languageDropdownRef}>
              <div
                className={`language-selector-trigger ${showLanguageDropdown ? 'open' : ''}`}
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              >
                <div className="course-generator-selected-language">
                  <IconRenderer
                    name={language.toLowerCase()}
                    type="language"
                    size="small"
                    className="language-icon"
                  />
                  <span>{language}</span>
                </div>
                <span className="dropdown-arrow">▼</span>
              </div>

              {showLanguageDropdown && (
                <div className="language-dropdown">
                  {PROGRAMMING_LANGUAGES.map(lang => (
                    <div
                      key={lang}
                      className={`language-option ${language === lang ? 'selected' : ''}`}
                      onClick={() => handleLanguageSelect(lang)}
                    >
                      <IconRenderer
                        name={lang.toLowerCase()}
                        type="language"
                        size="small"
                        className="language-icon"
                      />
                      <span>{lang}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="explore-back-info-form-group">
            <button
              type="button"
              className="explore-back-info-advanced-toggle"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? '▼' : '▶'} Advanced Options
            </button>

            {showAdvanced && (
              <div className="explore-back-info-advanced-options">
                <label htmlFor="topic-area" className="explore-back-info-form-label">
                  Topic Area (Optional)
                </label>
                <input
                  id="topic-area"
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., game_development, web_development, data_science"
                  className="explore-back-info-form-input"
                />
                <small className="explore-back-info-form-help">
                  This helps us categorize and organize your course better
                </small>
              </div>
            )}
          </div>

          {insufficientTokensError && (
            <div className="explore-back-info-token-error">
              <p>{insufficientTokensError}</p>
              <button
                type="button"
                className="explore-back-info-purchase-tokens-btn"
                onClick={() => {
                  const event = new CustomEvent('openTokenUI');
                  window.dispatchEvent(event);
                }}
              >
                Purchase Tokens
              </button>
            </div>
          )}

          <div className="explore-back-info-form-actions">
            <div className="generation-type-selector">
              <button
                type="button"
                className={`generation-type-btn ${generationType === 'course' ? 'active' : ''}`}
                onClick={() => setGenerationType('course')}
                disabled={isGenerating}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
                <span>Full Course</span>
              </button>
              <button
                type="button"
                className={`generation-type-btn ${generationType === 'cookbook' ? 'active' : ''}`}
                onClick={() => setGenerationType('cookbook')}
                disabled={isGenerating}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
                <span>Cookbook Only</span>
              </button>
            </div>
            <div className="explore-back-info-action-right">
              <div className="explore-back-info-token-info">
                <span className="explore-back-info-token-balance">
                  Your tokens: <strong>{tokenBalance}</strong>
                </span>
                <span className="explore-back-info-token-cost">
                  Cost: <strong>{estimatedCost} tokens</strong>
                </span>
              </div>
              <button
                type="submit"
                disabled={!prompt.trim() || isGenerating || !hasSufficientTokens}
                className={`explore-back-info-generate-btn ${!hasSufficientTokens ? 'insufficient-tokens' : ''}`}
                title={!hasSufficientTokens ? `You need ${estimatedCost} tokens. You have ${tokenBalance}.` : ''}
              >
                {isGenerating ? (
                  <>
                    <span className="spinner"></span>
                    {generationType === 'cookbook' ? 'Creating Cookbook...' : 'Creating Course...'}
                  </>
                ) : (
                  generationType === 'cookbook' ? 'Create Cookbook' : 'Create My Course'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExploreBackInfo;

